import { ChangeDetectionStrategy, Component, input, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, startWith, switchMap } from 'rxjs';
import { AtomModule } from '@shared/modules/color-scheme/atom/atom.module';
import { LinkModule } from '@shared/ui/kit/link/link.module';
import { LoaderNewComponent } from '@shared/ui/loader/loader-new.component';
import {
  NotificationContrast,
  NotificationItem,
  NotificationType
} from '@shared/ui/kit/notification/notification-item';

@Component({
  selector: 'loop-notification-card',
  templateUrl: './notification-card.component.html',
  styleUrls: ['./notification-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AtomModule, LinkModule, LoaderNewComponent]
})
export class NotificationCardComponent {
  readonly notificationSignal = input.required<NotificationItem>({ alias: 'notification' });

  private readonly notification$ = toObservable(this.notificationSignal);

  // switchMap follows whatever the caller keeps telling the notification while it is up: an update
  // replaces the item, which drops the old message$/isExpiring$ subscription and takes up the new one.
  protected readonly messageSignal: Signal<string | undefined> = toSignal(
    this.notification$.pipe(
      switchMap(notification => (notification.message$ ?? EMPTY).pipe(startWith(notification.message)))
    )
  );

  /** Set once a countdown is nearly up, which is when it takes on the warning treatment. */
  protected readonly isExpiringSignal: Signal<boolean> = toSignal(
    this.notification$.pipe(switchMap(notification => (notification.isExpiring$ ?? EMPTY).pipe(startWith(false)))),
    { initialValue: false }
  );

  protected readonly notificationType: typeof NotificationType = NotificationType;
  protected readonly notificationContrast: typeof NotificationContrast = NotificationContrast;
}
